import { isDefined } from '@poseidon-city/utils';
import axios from 'axios';

import { TApiSDKOptions } from './main';

export type TSSEHandler = (event: string, payload: any) => void;

type TAPIClient = {
  setAuthToken(accessToken: string): void;
  post<TPBody, TPResult>(path: string, body?: TPBody): Promise<TPResult | undefined>;
  put<TPBody, TPResult>(path: string, body?: TPBody): Promise<TPResult | undefined>;
  get<TPResult>(path: string): Promise<TPResult | undefined>;
  delete(path: string): Promise<void>;
  stream(path: string, onEvent: TSSEHandler, signal: AbortSignal): Promise<void>;
};

type TRequestIdService = {
  /**
   * Generates a unique ID for each request
   * @return {string} Unique ID (length: 20 characters)
   */
  getUniqueId(): string;
};

type TLanguageService = {
  /**
   * Returns the currently selected language (e.g., 'en', 'cs')
   */
  getSelectedLanguage(): string;
};

function APIClient(
  options: TApiSDKOptions,
  requestIdService: TRequestIdService,
  languageService: TLanguageService
): TAPIClient {
  let baseURL = `${options.host}${options.pathPrefix}`;

  if (baseURL.endsWith('/')) {
    baseURL = baseURL.slice(0, -1);
  }

  const client = axios.create({
    baseURL,
    timeout: 30000,
  });

  let _accessToken: string | undefined;

  client.interceptors.request.use((config) => {
    if (isDefined(_accessToken)) {
      config.headers.Authorization = `Bearer ${_accessToken}`;
    }

    config.headers['X-Request-Id'] = requestIdService.getUniqueId();
    config.headers['Accept-Language'] = languageService.getSelectedLanguage();

    // Handle FormData content type
    const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;

    if (!isFormData) {
      config.headers['Content-Type'] = 'application/json';
    } else {
      delete (config.headers as any)['Content-Type'];
    }

    return config;
  });

  const getHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {
      'X-Request-Id': requestIdService.getUniqueId(),
      'Accept-Language': languageService.getSelectedLanguage(),
    };

    if (isDefined(_accessToken)) {
      headers.Authorization = `Bearer ${_accessToken}`;
    }

    return headers;
  };

  const request = async (method: string, url: string, data?: any): Promise<any> => {
    const response = await client.request({
      method,
      url,
      data,
    });

    if (response.status >= 400) {
      console.error(`APIClient.${method} '${url}' failed`, response);
      return;
    }

    return response.data;
  };

  return {
    setAuthToken(accessToken: string): void {
      _accessToken = accessToken;
    },

    async post<TPBody, TPResult>(path: string, body: TPBody): Promise<TPResult | undefined> {
      return request('post', path, body) as TPResult | undefined;
    },

    async put<TPBody, TPResult>(path: string, body: TPBody): Promise<TPResult | undefined> {
      return request('put', path, body) as TPResult | undefined;
    },

    async get<TPResult>(path: string): Promise<TPResult | undefined> {
      return request('get', path) as TPResult | undefined;
    },

    async delete(path: string): Promise<void> {
      return request('delete', path);
    },

    async stream(path: string, onEvent: TSSEHandler, signal: AbortSignal): Promise<void> {
      const url = `${baseURL}${path.startsWith('/') ? path : `/${path}`}`;
      const headers = getHeaders();
      headers.Accept = 'text/event-stream';

      const res = await fetch(url, {
        method: 'GET',
        headers,
        signal,
      });

      if (res.status === 401) {
        throw new Error('Unauthorized (401) – missing/invalid token for SSE stream.');
      }
      if (!res.ok) {
        throw new Error(`SSE failed: ${res.status} ${res.statusText}`);
      }
      if (!res.body) {
        throw new Error('SSE failed: response has no body (stream not supported).');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');

      let buffer = '';
      let currentEvent = 'message';
      let dataLines: string[] = [];

      const flushEvent = (): void => {
        const dataRaw = dataLines.join('\n');
        dataLines = [];

        if (!dataRaw) {
          onEvent(currentEvent, null);
          currentEvent = 'message';
          return;
        }

        try {
          onEvent(currentEvent, JSON.parse(dataRaw));
        } catch {
          onEvent(currentEvent, dataRaw);
        }
        currentEvent = 'message';
      };

      while (!signal.aborted) {
        let result: ReadableStreamReadResult<Uint8Array>;

        try {
          result = await reader.read();
        } catch (e: any) {
          if (signal.aborted || e?.name === 'AbortError') return;
          throw e;
        }

        const { value, done } = result;
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let idx: number;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const chunk = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);

          if (chunk.trim().startsWith(':')) continue;

          currentEvent = 'message';
          dataLines = [];

          for (const rawLine of chunk.split('\n')) {
            const line = rawLine.replace(/\r$/, '');
            if (!line) continue;

            if (line.startsWith('event:')) currentEvent = line.slice(6).trim();
            else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
          }

          flushEvent();
        }
      }
    },
  };
}

export { APIClient };
export type { TAPIClient, TRequestIdService, TLanguageService };
